import React, { useState } from 'react';

import { Box, Button, Header, Modal, Pagination, SpaceBetween, Table } from '@amzn/awsui-components-react-v3';

import type { MPNToIPNMapping, MPNToIPNPair } from '../../store/types';
import { useCollection } from '@amzn/awsui-collection-hooks';

interface MPNToIPNMappingProps {
  mpnToIPNMapping: MPNToIPNMapping;
  onDismiss?: () => void;
  onConfirm?: (model: any) => void;
}

export const ManufacturerSelectionModal = ({
                                             mpnToIPNMapping,
                                             onDismiss = () => { },
                                             onConfirm = () => { },
                                 }: MPNToIPNMappingProps) => {
  const [visible, setVisible] = useState(true);
  const [selectedItems, setSelectedItems] = useState<MPNToIPNPair[]>([]);

  const closeModal = () => {
    setVisible(false);
    onDismiss();
  };

  const confirmSelection = () => {
    setVisible(false);
    onConfirm(selectedItems[0]);
  };

  const associatedManufacturerSet: Set<string> = new Set();
  const associatedItems: MPNToIPNPair[] = Array.isArray(mpnToIPNMapping?.associatedIpns) ? [...mpnToIPNMapping?.associatedIpns] : [];
  associatedItems.forEach((entry: MPNToIPNPair): void => {
    associatedManufacturerSet.add(entry.manufacturer);
  });

  // pagination props
  const { items, paginationProps } = useCollection([...associatedManufacturerSet], {
    pagination: { pageSize: 25 },
    sorting: {},
  });

  return (
    <Box>
      <Modal
        onDismiss={closeModal}
        visible={visible}
        size="large"
        closeAriaLabel="Close modal"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" ariaLabel="Cancel manufacturer selection dialog" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                ariaLabel="Confirm selected manufacturer"
                disabled={selectedItems.length === 0}
                onClick={confirmSelection}
              >
                Confirm
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={
          <Header
            variant="h2"
            actions={null}
            description="Choose the manufacturer of the part(s) being received."
          >
            Multiple Manufacturers found in Townsend - {mpnToIPNMapping?.mpn}
          </Header>
        }
      >
        <Table
          onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
          selectedItems={selectedItems}
          ariaLabels={{
            selectionGroupLabel: 'Manufacturer selection',
            allItemsSelectionLabel: ({ selectedItems }) =>
              `${selectedItems.length} ${selectedItems.length === 1 ? 'item' : 'items'} selected`,
            itemSelectionLabel: ({ selectedItems }, item) => {
              const isItemSelected = selectedItems.find((i) => i === item);
              return `${item} is ${isItemSelected ? 'selected' : 'not selected'}`;
            },
          }}
          columnDefinitions={[
            {
              id: 'manufacturer',
              header: 'Manufacturer',
              cell: (e: string) => e,
            },
          ]}
          pagination={<Pagination {...paginationProps} />}
          items={items}
          loadingText="Loading resources"
          selectionType="single"
          onRowClick={({ detail }: any) => {
            setSelectedItems([detail.item]);
          }}
          visibleColumns={['manufacturer']}
        />
      </Modal>
    </Box>
  );
};
